import { ContactType } from '@/core/domain/_shared/ContactType';
import { ReportChecklistData } from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.types';
import { applyCpfCnpjMask, applyMask, removeMask } from '@/utils/mask';
import {
  cellphoneMask,
  cepMask,
  licensePlateMask,
  phoneMask,
} from '@/utils/mask/commonMasks';
import formatterNumber from '@/utils/number/formatterNumber';
import moment from 'moment';

interface Props {
  checklist?: ReportChecklistData;
  isEmpty?: boolean;
  infoCustomerVisible?: boolean;
}

const ReportChecklistDescription: React.FC<Props> = ({
  checklist,
  isEmpty,
  infoCustomerVisible,
}) => {
  const isBosch = checklist?.company?.bosch;
  const hasAddressFields =
    checklist?.customer?.addressResponse &&
    Object.values(checklist?.customer?.addressResponse).some((v) => !!v);

  const phoneNumber = checklist?.customer?.contacts
    ?.filter(
      (contact) =>
        contact.contactType !== ContactType.Email &&
        contact.contactType !== ContactType.Fax,
    )
    .map((contact) =>
      applyMask(
        removeMask(contact.value),
        removeMask(contact.value).length < 11 ? phoneMask : cellphoneMask,
      ),
    )
    .join(', ');

  const email = checklist?.customer?.contacts?.find(
    (contact) => contact.contactType === ContactType.Email,
  )?.value;

  return (
    <div className="reportChecklist-description">
      <div className="reportChecklist-description-info">
        {infoCustomerVisible && (
          <>
            <div className="reportChecklist-description-item w-50">
              <label className="reportChecklist-description-label">
                Nome do cliente:
              </label>
              <span>{checklist?.customer?.name || isEmpty || '-'}</span>
            </div>

            {!isBosch && (
              <div className="reportChecklist-description-item w-50">
                <label className="reportChecklist-description-label">
                  {!!checklist?.customer?.documentNumber && (
                    <>
                      {(checklist?.customer?.documentNumber?.length || 0) > 11
                        ? 'CNPJ:'
                        : 'CPF:'}
                    </>
                  )}

                  {!!checklist?.customer?.foreignDocument &&
                    'Documento estrangeiro'}
                </label>
                <span>
                  {!!checklist?.customer?.documentNumber && (
                    <>
                      {!isEmpty &&
                        applyCpfCnpjMask(
                          checklist?.customer?.documentNumber || '-',
                        )}
                    </>
                  )}

                  {!!checklist?.customer?.foreignDocument && (
                    <>{!isEmpty && checklist?.customer?.foreignDocument}</>
                  )}
                </span>
              </div>
            )}

            <div className="reportChecklist-description-item w-50">
              <label className="reportChecklist-description-label">
                Telefone:
              </label>
              <span>{phoneNumber ?? (isEmpty || '-')}</span>
            </div>

            <div className="reportChecklist-description-item w-50">
              <label className="reportChecklist-description-label">
                E-mail:
              </label>
              <span>{email || isEmpty || '-'}</span>
            </div>

            <div className="reportChecklist-description-item w-100">
              <label className="reportChecklist-description-label">
                Endereço:
              </label>
              <span>
                {!hasAddressFields && (isEmpty || '-')}
                {hasAddressFields &&
                  `
            ${
              checklist?.customer?.addressResponse.street
                ? `${checklist?.customer?.addressResponse.street}, `
                : ''
            }
            ${
              checklist?.customer?.addressResponse.number
                ? `${checklist?.customer?.addressResponse.number}, `
                : ''
            }
            ${
              checklist?.customer?.addressResponse.complement
                ? `${checklist?.customer?.addressResponse.complement}, `
                : ''
            }
            ${
              checklist?.customer?.addressResponse.district
                ? `${checklist?.customer?.addressResponse.district}, `
                : ''
            }
            ${
              checklist?.customer?.addressResponse.city
                ? `${checklist?.customer?.addressResponse.city} - `
                : ''
            }
            ${
              applyMask(
                checklist?.customer?.addressResponse.zipCode,
                cepMask,
              ) || ''
            }
            `}
              </span>
            </div>
          </>
        )}

        <div
          className={`reportChecklist-description-item ${
            infoCustomerVisible ? 'w-100' : 'w-50'
          }`}
        >
          <label className="reportChecklist-description-label">
            Marca/Modelo:
          </label>
          <span>{checklist?.vehicle?.model || isEmpty || '-'}</span>
        </div>

        <div className="reportChecklist-description-item w-25">
          <label className="reportChecklist-description-label">Ano:</label>
          <span>
            {!isEmpty &&
              `${checklist?.vehicle?.yearFabrication}/${checklist?.vehicle?.yearModel}`}
          </span>
        </div>

        <div className="reportChecklist-description-item w-25">
          <label className="reportChecklist-description-label">KM:</label>
          <span>
            {formatterNumber(checklist?.vehicle?.km || 0, {
              minimumFractionDigits: 0,
            }) ||
              isEmpty ||
              '-'}
          </span>
        </div>

        {infoCustomerVisible && (
          <>
            <div className="reportChecklist-description-item w-50">
              <label className="reportChecklist-description-label">
                Placa do Veículo:
              </label>
              <span>
                {applyMask(
                  checklist?.vehicle?.licensePlate,
                  licensePlateMask,
                ) ||
                  isEmpty ||
                  '-'}
              </span>
            </div>

            <div className="reportChecklist-description-item w-25">
              <label className="reportChecklist-description-label">
                Chassi:
              </label>
              <span>{checklist?.vehicle?.chassi || isEmpty || '-'}</span>
            </div>
          </>
        )}
      </div>

      <div className="reportChecklist-description-os">
        <div className="reportChecklist-description-item w-100">
          <label className="reportChecklist-description-label">
            N° do orçamento:
          </label>
          <span>{checklist?.worKOrder?.number || isEmpty || '-'}</span>
        </div>

        {isBosch ? (
          <div className="reportChecklist-description-item w-100">
            <label className="reportChecklist-description-label">Data:</label>
            <span>
              {checklist?.worKOrder?.entryDate
                ? moment(checklist?.worKOrder?.entryDate).format('DD/MM/YYYY')
                : '-'}
            </span>
          </div>
        ) : (
          <div className="reportChecklist-description-item w-100">
            <label className="reportChecklist-description-label">
              Emissão:
            </label>
            <span>{!isEmpty && moment().format('DD/MM/YYYY')}</span>
          </div>
        )}

        {isBosch ? (
          <div className="reportChecklist-description-item w-100">
            <label className="reportChecklist-description-label">
              Horário de início:
            </label>
            <span>
              {checklist?.worKOrder?.entryDate
                ? moment(checklist?.worKOrder?.entryDate).format(
                    'DD/MM/YYYY HH:mm',
                  )
                : '-'}
            </span>
          </div>
        ) : (
          <div className="reportChecklist-description-item w-100">
            <label className="reportChecklist-description-label">
              Entrada:
            </label>
            <span>
              {checklist?.worKOrder?.entryDate
                ? moment(checklist?.worKOrder?.entryDate).format('DD/MM/YYYY')
                : isEmpty || '-'}
            </span>
          </div>
        )}

        {isBosch ? (
          <div className="reportChecklist-description-item w-100">
            <label className="reportChecklist-description-label">
              Horário de termino:
            </label>
            <span>
              {checklist?.worKOrder?.closingDate
                ? moment(checklist?.worKOrder?.closingDate).format(
                    'DD/MM/YYYY HH:mm',
                  )
                : '-'}
            </span>
          </div>
        ) : (
          <div className="reportChecklist-description-item w-100">
            <label className="reportChecklist-description-label">Saída:</label>
            <span>
              {checklist?.worKOrder?.closingDate
                ? moment(checklist?.worKOrder?.closingDate).format('DD/MM/YYYY')
                : isEmpty || '-'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportChecklistDescription;
